import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { CheckCircle2, FileSearch, Loader2, Undo2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLocalization } from '@/hooks/useLocalization';
import { REGISTER_KINDS, useProjectIntel, type IntelItem, type RegisterEntry } from '@/hooks/useProjectIntel';

interface Props {
  projectId: string;
  canManage: boolean;
  onChanged?: () => void;
}

const RATING_STYLE: Record<string, string> = {
  high: 'bg-destructive/15 text-destructive',
  medium: 'bg-amber-500/15 text-amber-600',
  low: 'bg-emerald-500/15 text-emerald-600',
};

const SECTION_LABEL = { risk: 'Risks', issue: 'Issues', assumption: 'Assumptions' } as const;

const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : null);
const num = (v: unknown) => (typeof v === 'number' ? v : null);

/** Words for facts that are stored but have no screen to approve them in yet. */
const OTHER_KIND_LABEL: Record<string, string> = {
  dependency: 'dependencies', stakeholder: 'stakeholders', team_member: 'team members',
  change_request: 'change requests', contract: 'contract lines', lesson: 'lessons', metric: 'metrics',
};

/** "2 - High" shows as "high". */
const severityWord = (v: string | null) => v?.match(/\b(critical|high|medium|low)\b/i)?.[1].toLowerCase() ?? v;

const isOpen = (status: string | null) => !status || !/^(closed|resolved|done|retired|rejected)/.test(status);

function RatingChip({ rating, score }: { rating: string | null; score: number | null }) {
  const { tx } = useLocalization();
  if (!rating && score === null) return null;
  return (
    <span className={cn('inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium', RATING_STYLE[rating ?? ''] ?? 'bg-muted text-muted-foreground')}>
      {rating ? tx(rating) : ''}{rating && score !== null ? ' · ' : ''}{score !== null ? score : ''}
    </span>
  );
}

function Meta({ parts }: { parts: Array<string | null> }) {
  const shown = parts.filter((p): p is string => !!p);
  if (shown.length === 0) return null;
  return <p className="mt-0.5 text-xs text-muted-foreground">{shown.join(' · ')}</p>;
}

function RegisterRow({ e }: { e: RegisterEntry }) {
  const { tx } = useLocalization();
  return (
    <li className="flex items-start justify-between gap-3 rounded-md border p-3">
      <div className="min-w-0">
        <p className="text-sm font-medium leading-snug">{tx(e.title)}</p>
        <Meta parts={[e.ref, e.category, e.owner, e.status, e.validity]} />
        {e.mitigation && <p className="mt-1 text-xs text-muted-foreground">{tx(e.mitigation)}</p>}
      </div>
      <RatingChip rating={e.rating ?? severityWord(e.severity)} score={e.score} />
    </li>
  );
}

function DetectedRow({ item, checked, onToggle, disabled }: { item: IntelItem; checked: boolean; onToggle: () => void; disabled: boolean }) {
  const { tx } = useLocalization();
  const p = item.payload;
  const title = str(p.title) ?? str(p.text) ?? item.item_key;
  const score = num(p.score) ?? (num(p.probability) !== null && num(p.impact) !== null ? (num(p.probability) as number) * (num(p.impact) as number) : null);
  return (
    <li className="flex items-start gap-3 rounded-md border p-3">
      <Checkbox checked={checked} onCheckedChange={onToggle} disabled={disabled} className="mt-0.5" aria-label={title} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium leading-snug">{tx(title)}</p>
        <Meta parts={[str(p.ref) ?? item.item_key, str(p.category), str(p.owner), str(p.status), str(p.validity)]} />
      </div>
      <RatingChip rating={str(p.rating) ?? severityWord(str(p.severity))} score={score} />
    </li>
  );
}

export function DocumentedRisksPanel({ projectId, canManage, onChanged }: Props) {
  const { t } = useLocalization();
  const { dossierKey, items, register, loading, busy, error, approve, undo } = useProjectIntel(projectId, onChanged);
  const [unticked, setUnticked] = useState<Set<string>>(new Set());
  const [confirmUndo, setConfirmUndo] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const suggested = useMemo(() => items.filter((i) => i.status === 'suggested' && REGISTER_KINDS.includes(i.kind)), [items]);
  const others = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const i of items) if (i.status === 'suggested' && !REGISTER_KINDS.includes(i.kind)) counts[i.kind] = (counts[i.kind] ?? 0) + 1;
    return Object.entries(counts).map(([k, n]) => `${n} ${OTHER_KIND_LABEL[k] ?? k}`);
  }, [items]);
  const fromDocuments = register.filter((r) => r.intel_item_id).length;
  const toApprove = suggested.length - suggested.filter((i) => unticked.has(i.id)).length;

  const toggle = (id: string) => setUnticked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const onApprove = async () => {
    setNotice(null);
    const res = await approve([...unticked].filter((id) => suggested.some((s) => s.id === id)));
    if (res.ok) {
      setUnticked(new Set());
      setNotice(`${res.result.approved} added to the register${res.result.rejected ? `, ${res.result.rejected} skipped` : ''}.`);
    }
  };

  const onUndo = async () => {
    if (!confirmUndo) { setConfirmUndo(true); return; }
    setConfirmUndo(false);
    setNotice(null);
    const res = await undo();
    if (res.ok) {
      setNotice(`${res.result.reverted} moved back to review${res.result.skipped.length ? `; ${res.result.skipped.length} edited since approval were left in the register` : ''}.`);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-10 text-sm text-muted-foreground"><Loader2 className="me-2 h-4 w-4 animate-spin" />{t('Loading register')}</div>;
  }

  const grouped = (['risk', 'issue', 'assumption'] as const).map((type) => ({ type, rows: register.filter((r) => r.entry_type === type) }));
  const groupedSuggested = (['risk', 'issue', 'assumption'] as const).map((kind) => ({ kind, rows: suggested.filter((i) => i.kind === kind) }));

  return (
    <div className="space-y-5">
      {suggested.length > 0 && (
        <section aria-label={t('Detected from documents')} className="rounded-lg border border-primary/30 bg-primary/5 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <FileSearch className="h-4 w-4 text-primary" aria-hidden="true" />
              <h3 className="text-sm font-semibold">{t('Detected from documents')}</h3>
              <Badge variant="secondary">{suggested.length}</Badge>
            </div>
            {canManage && (
              <Button size="sm" onClick={onApprove} disabled={busy || toApprove === 0}>
                {busy ? <Loader2 className="me-1.5 h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="me-1.5 h-3.5 w-3.5" />}
                {t('Add to register')} ({toApprove})
              </Button>
            )}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {t('Read from this project’s RAID log. Untick anything that should not go in the register.')}
          </p>
          <div className="mt-3 space-y-4">
            {groupedSuggested.filter((g) => g.rows.length > 0).map((g) => (
              <div key={g.kind}>
                <h4 className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">{t(SECTION_LABEL[g.kind])} ({g.rows.length})</h4>
                <ul className="space-y-2">
                  {g.rows.map((i) => (
                    <DetectedRow key={i.id} item={i} checked={!unticked.has(i.id)} onToggle={() => toggle(i.id)} disabled={busy || !canManage} />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      )}

      {others.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {t('Also found in this project’s documents, kept for review once their tabs are connected:')} {others.join(', ')}.
        </p>
      )}

      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      {notice && <p role="status" className="text-sm text-muted-foreground">{notice}</p>}

      {register.length === 0 ? (
        <div className="py-6 text-center text-sm text-muted-foreground">
          {dossierKey ? t('Nothing in the register yet. Add the detected items above.') : t('No documents are linked to this project, so there is nothing to detect.')}
        </div>
      ) : (
        <>
          {grouped.filter((g) => g.rows.length > 0).map((g) => (
            <section key={g.type} aria-label={t(SECTION_LABEL[g.type])}>
              <h3 className="mb-2 text-sm font-semibold">
                {t(SECTION_LABEL[g.type])} ({g.rows.length})
                {g.type === 'risk' && <span className="ms-2 text-xs font-normal text-muted-foreground">{g.rows.filter((r) => isOpen(r.status)).length} {t('open')}</span>}
              </h3>
              <ul className="space-y-2">{g.rows.map((r) => <RegisterRow key={r.id} e={r} />)}</ul>
            </section>
          ))}
          {canManage && fromDocuments > 0 && (
            <div className="flex justify-end">
              <Button variant="ghost" size="sm" onClick={onUndo} disabled={busy} className="text-muted-foreground">
                <Undo2 className="me-1.5 h-3.5 w-3.5" />
                {confirmUndo ? t('Click again to move them back to review') : t('Undo import')}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default DocumentedRisksPanel;
